import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TCustomerTransferReduxState } from "./types";
import { TCreateCustomerTransferFinalDraftsParams, TCustomerTransferListing, TGetCustomerDetailsData, TGetCustomerTransferListingFilters, TGetCustomerTransferSummaryData } from "~types/feat/customerTransfer";
import dayjs from "dayjs";
import { TGetCustomerTransferListingAPIResponse } from "~types/feat/customerTransfer";
import { FundTypeEnum, MetalTypeEnum, TransactionStatusEnum } from "~utils/enums";

const getFirstDayOfMonth = () => {
    const today = dayjs();

    const firstDay = today.startOf('month');

    return firstDay.toISOString();

};

const getTodaysDate = () => {
    const today = dayjs().endOf('day').toISOString();

    return today;
};

export const initialState: TCustomerTransferReduxState = {
    loadings: {
        getCustomerTransferListing: false,
        getCustomerTransferListingExport: false,
        createCustomerTransferDrafts: false,
        createCustomerTransferFinalDrafts: false,
        createNewCustomerTransfer: false,
        getCustomerTransferSummary: false,
        getCustomerTransferDetails: false,
    },
    customerTransferListing: {
        index: 0,
        maxIndex: 0,
        totalCount: 0,
        filters: {
            dateFrom: getFirstDayOfMonth(),
            dateTo: getTodaysDate(),
            branchCode: '',
            branchName: '',
            metalType: '',
            senderName: '',
            senderIdNo: '',
            receiverName: '',
            receiverIdNo: '',
            status: '',
            productName: '',
            productId: 0,
        },
        data: [],
    },
    customerTransferExportListingForExport: [],
    selectedCustomerTransfer: undefined,
    customerTransferSummary: {
        draftId: 0,
        senderCustomerId: 0,
        senderName: '',
        senderIdNo: '',
        senderCIFNo: '',
        receiverCustomerId: 0,
        receiverName: '',
        receiverIdNo: '',
        receiverCIFNo: '',
        productId: 0,
        productName: '',
        metalType: MetalTypeEnum.Gold,
        weight: 0,
        branchId: 0,
        branchCode: '',
        branchName: '',
        regionName: '',
        branchTellerName: '',
        salesPersonnelName: '',
        referralName: '',
        status: TransactionStatusEnum.Approved,
        totalAmount: '',
    },
    customerTransferDetails: {
        customerTransferId: 0,
        senderCustomerId: 0,
        senderName: '',
        senderCIFNo: '',
        senderIdNo: '',
        productId: 0,
        productName: '',
        metalType: MetalTypeEnum.Gold,
        fundType: FundTypeEnum.Conventional,
        serialNumbers: [],
        weight: 0,
        status: TransactionStatusEnum.Approved,
        receiverCustomerId: 0,
        receiverName: '',
        receiverCIFNo: '',
        receiverIdNo: '',
        branchCode: '',
        branchName: '',
        regionName: '',
        branchTellerName: '',
        salesPersonnelName: '',
        referralName: '',
        approvedByName: '',
        rejectedByName: '',
        approvedAt: '',
        rejectedAt: '',
        createdAt: '',
        items: [],
        totalAmount: '',
    },
    customerTransferDraftId: {
        draftId: 0,
    },
    modals: {
        isCustomerTransferDetailsModalOpen: false,
        createCustomerTransferModalIsOpen: false,
        isFormFilled: false,
    },
    errors: {
        getCustomerTransferListing: '',
        getCustomerTransferListingExport: '',
        createCustomerTransferDrafts: '',
        createCustomerTransferFinalDrafts: '',
        createNewCustomerTransfer: '',
        getCustomerTransferSummary: '',
        getCustomerTransferDetails: '',
    },
}

const customerTransferSlice = createSlice({
    name: 'customerTransfer',
    initialState,
    reducers: {
        customerTransferGetCustomerTransferListingAttempt: (state, action: PayloadAction<{ customerId?: number } | undefined>) => {
            state.loadings.getCustomerTransferListing = true;
            state.errors.getCustomerTransferListing = '';
        },
        customerTransferGetCustomerTransferListingSuccess: (state, action: PayloadAction<TGetCustomerTransferListingAPIResponse>) => {
            state.loadings.getCustomerTransferListing = false;
            state.errors.getCustomerTransferListing = '';
            state.customerTransferListing = {
                filters: state.customerTransferListing.filters,
                ...action.payload,
            };
        },
        customerTransferGetCustomerTransferListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getCustomerTransferListing = false;
            if (action.payload) state.errors.getCustomerTransferListing = action.payload;
        },
        customerTransferGetCustomerTransferListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.customerTransferListing.index = index;
        },

        customerTransferSetCustomerTransferListingFilters: (state, action: PayloadAction<TGetCustomerTransferListingFilters>) => {
            state.customerTransferListing.filters = action.payload;
        },
        customerTransferSetCustomerTransferListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.customerTransferListing.index = index;
        },

        customerTransferGetExportCustomerTransferAttempt: (state) => {
            state.loadings.getCustomerTransferListingExport = true;
            state.errors.getCustomerTransferListingExport = '';
        },
        customerTransferGetExportCustomerTransferSuccess: (state, action: PayloadAction<TCustomerTransferListing[]>) => {
            state.loadings.getCustomerTransferListingExport = false;
            state.customerTransferExportListingForExport = action.payload;
        },
        customerTransferGetExportCustomerTransferFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getCustomerTransferListingExport = false;
            if (action.payload) state.errors.getCustomerTransferListingExport = action.payload;
        },
        customerTransferClearCustomerTransferListingForExport: (state) => {
            state.customerTransferExportListingForExport = initialState.customerTransferExportListingForExport;
        },

        customerTransferSetSelectedCustomerTransfer: (state, action: PayloadAction<TCustomerTransferListing | undefined>) => {
            state.selectedCustomerTransfer = action.payload;
        },

        customerTransferGetCustomerTransferSummaryAttempt: (state, action: PayloadAction<{ id: number }>) => {
            state.loadings.getCustomerTransferSummary = true;
            state.errors.getCustomerTransferSummary = '';
        },
        customerTransferGetCustomerTransferSummarySuccess: (state, action: PayloadAction<TGetCustomerTransferSummaryData>) => {
            state.loadings.getCustomerTransferSummary = false;
            state.errors.getCustomerTransferSummary = '';

            state.customerTransferSummary = action.payload;
        },
        customerTransferGetCustomerTransferSummaryFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getCustomerTransferSummary = false;
            if (action.payload) state.errors.getCustomerTransferSummary = action.payload;
        },

        // Modals
        customerTransferSetIsCustomerTransferDetailsModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isCustomerTransferDetailsModalOpen = action.payload;
        },
        customerTransferSetCreateCustomerTransferModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.createCustomerTransferModalIsOpen = action.payload;
        },

        // create customer draft
        customerTransferCreateCustomerTransferDraftAttempt: (state) => {
            state.loadings.createCustomerTransferDrafts = true;
            state.errors.createCustomerTransferDrafts = '';
        },
        customerTransferCreateCustomerTransferDraftSuccess: (state, action: PayloadAction<{ draftId: number }>) => {
            state.loadings.createCustomerTransferDrafts = false;
            state.errors.createCustomerTransferDrafts = '';
            state.customerTransferDraftId = action.payload;
        },
        customerTransferCreateCustomerTransferDraftFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createCustomerTransferDrafts = false;
            if (action.payload) state.errors.createCustomerTransferDrafts = action.payload;
        },

        // create customer transfer final drafts
        customerTransferCreateCustomerTransferFinalDraftAttempt: (state, action: PayloadAction<TCreateCustomerTransferFinalDraftsParams>) => {
            state.loadings.createCustomerTransferFinalDrafts = true;
            state.errors.createCustomerTransferFinalDrafts = '';
        },
        customerTransferCreateCustomerTransferFinalDraftSuccess: (state) => {
            state.loadings.createCustomerTransferFinalDrafts = false;
            state.errors.createCustomerTransferFinalDrafts = '';
        },
        customerTransferCreateCustomerTransferFinalDraftFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createCustomerTransferFinalDrafts = false;
            if (action.payload) state.errors.createCustomerTransferFinalDrafts = action.payload;
        },

        customerTransferCreateNewCustomerTransferAttempt: (state, action: PayloadAction<{ draftId: number }>) => {
            state.loadings.createNewCustomerTransfer = true;
            state.errors.createNewCustomerTransfer = '';
        },
        customerTransferCreateNewCustomerTransferSuccess: (state) => {
            state.loadings.createNewCustomerTransfer = false;
            state.errors.createNewCustomerTransfer = '';
        },
        customerTransferCreateNewCustomerTransferFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createNewCustomerTransfer = false;
            if (action.payload) state.errors.createNewCustomerTransfer = action.payload;
        },
        customerTransferResetCreateNewCustomerTransferState: (state) => {
            state.loadings.createNewCustomerTransfer = initialState.loadings.createNewCustomerTransfer;
            state.errors.createNewCustomerTransfer = initialState.errors.createNewCustomerTransfer;
        },

        customerTransferGetCustomerTransferDetailsAttempt: (state, action: PayloadAction<{ id: number}>) => {
            state.loadings.getCustomerTransferDetails = true;
            state.errors.getCustomerTransferDetails = '';
        },
        customerTransferGetCustomerTransferDetailsSuccess: (state, action: PayloadAction<TGetCustomerDetailsData>) => {
            state.loadings.getCustomerTransferDetails = false;
            state.errors.getCustomerTransferDetails = '';
            state.customerTransferDetails = action.payload;
        },
        customerTransferGetCustomerTransferDetailsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getCustomerTransferDetails = false;
            if (action.payload) state.errors.getCustomerTransferDetails = action.payload;
        },

        customerTransferSetIsFormFilled: (state, action: PayloadAction<boolean>) => {
            state.modals.isFormFilled = action.payload;
        },
    }
});

export default {
    actions: customerTransferSlice.actions,
    reducers: customerTransferSlice.reducer,
}